import React, { useState } from "react";
import DSGrid from "../../system-design/DSGrid";
import CalendarNavigation from "./CalendarNavigation";
import CalendarControls from "./CalendarControls";
import CalendarGrid from "./CalendarGrid";
import useCalendarEvents from "../hooks/useCalendarEvents";
import useLoadKlydos from "../hooks/useLoadKlydos";
import {
  GeographicRegion,
  KLYDO_EVENT_PRIMARY_TYPE,
  PopulatedCalendarEvent,
  State,
} from "../../../api/calendarEvent";

type MonthlyCalendarViewProps = {
  defaultCountry?: GeographicRegion | null;
  defaultState?: State | null;
};

const MonthlyCalendarView = ({
  defaultCountry = null,
  defaultState = null,
}: MonthlyCalendarViewProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [country, setCountry] = useState(defaultCountry);
  const [state, setState] = useState(defaultState);
  const stateIdentifier = state?.iso || state?.abbrev;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const {
    data: allCalendarEvents,
    isLoading: eventsLoading,
    error: eventsError,
  } = useCalendarEvents({
    country: country?.code,
    year,
    month,
    state: stateIdentifier,
    populated: true,
    enabled: true,
  });

  const {
    populatedEvents,
    klydosLoading,
    klydosError,
    handleLoadKlydos,
    klydosLoaded,
  } = useLoadKlydos({
    country: country?.code,
    year,
    month,
    state: stateIdentifier,
    enabled: !!country,
  });

  const events = (allCalendarEvents as PopulatedCalendarEvent[]) || [];

  const holidayEvents: PopulatedCalendarEvent[] = (
    populatedEvents ||
    events.filter(
      (calendarEvent) => calendarEvent.primaryType !== KLYDO_EVENT_PRIMARY_TYPE,
    )
  ).map((e) => ({ ...e, klydos: e.klydos || [] }));

  const klydoPopulatedEvents: PopulatedCalendarEvent[] = events.filter(
    (calendarEvent) => calendarEvent.primaryType === KLYDO_EVENT_PRIMARY_TYPE,
  );

  const allEvents = [...holidayEvents, ...klydoPopulatedEvents];

  const handleNavigate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  return (
    <DSGrid container spacing={3}>
      <DSGrid item xs={12}>
        <CalendarControls
          country={country}
          state={state}
          onCountryChange={setCountry}
          onStateChange={setState}
          onLoadKlydos={handleLoadKlydos}
          klydosLoading={klydosLoading}
          klydosError={klydosError}
          klydosLoaded={klydosLoaded}
        />
      </DSGrid>

      <DSGrid item xs={12}>
        <CalendarNavigation
          currentDate={currentDate}
          onNavigate={handleNavigate}
        />
      </DSGrid>

      <DSGrid item xs={12}>
        <CalendarGrid
          currentDate={currentDate}
          events={allEvents}
          country={country?.code || ""}
          isLoading={eventsLoading || klydosLoading}
          error={eventsError || klydosError}
          hasCountry={!!country}
        />
      </DSGrid>
    </DSGrid>
  );
};

export default MonthlyCalendarView;
